import { resolvePrimaryEnvironmentHttpUrl } from "../../environments/primary/target";
import { buildPrimaryFetchInit } from "../../environments/primary/fetchInit";
import {
  PLUGIN_FALLBACKS,
  sortedPlugins,
  type ForgePlugin,
  type ForgePluginId,
} from "./pluginsPage.logic";

interface PluginsResponse {
  readonly connectors?: ReadonlyArray<ForgePlugin>;
  readonly data?: {
    readonly connectors?: ReadonlyArray<ForgePlugin>;
  };
}

export interface InstallResponse {
  readonly authUrl?: string;
  readonly alreadyConnected?: boolean;
  readonly error?: string;
  readonly message?: string;
}

interface GitHubStatusResponse {
  readonly connected?: boolean;
  readonly github_login?: string;
  readonly user_repo_access_usable?: boolean;
  readonly error?: string;
  readonly code?: string;
}

interface GitHubAuthorizeResponse {
  readonly url?: string;
  readonly error?: string;
  readonly message?: string;
}

const SETUP_OPTIONAL_FETCH_TIMEOUT_MS = 2_500;

async function fetchWithTimeout(
  input: string,
  init: RequestInit,
  timeoutMs = SETUP_OPTIONAL_FETCH_TIMEOUT_MS,
): Promise<Response> {
  const controller = new AbortController();
  const timeoutId = globalThis.setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(input, { ...init, signal: controller.signal });
  } finally {
    globalThis.clearTimeout(timeoutId);
  }
}

function mergePluginDefaults(plugin: ForgePlugin): ForgePlugin {
  const fallback = PLUGIN_FALLBACKS.find((entry) => entry.id === plugin.id);
  return fallback ? { ...fallback, ...plugin } : plugin;
}

function githubStatusToPlugin(payload: GitHubStatusResponse): ForgePlugin {
  const connected = payload.connected === true;
  const repoAccessUsable = payload.user_repo_access_usable !== false;
  const authReady = connected && repoAccessUsable;
  return {
    id: "github",
    name: payload.github_login ? `GitHub (${payload.github_login})` : "GitHub",
    description: "Create repositories, open pull requests, and manage workflow runs.",
    status: authReady ? "installed" : connected ? "error" : "pending",
    authReady,
    authReason: authReady ? null : connected ? "repo_access_unavailable" : "not_installed",
    accountLabel: payload.github_login ?? null,
    connectionKind: "github",
  };
}

async function fetchGitHubPlugin(): Promise<ForgePlugin> {
  const response = await fetch("/.well-known/forge/github/status", {
    headers: { Accept: "application/json" },
    credentials: "include",
  });
  const payload = (await response.json().catch(() => ({}))) as GitHubStatusResponse;
  if (response.status === 401 || payload.code === "CMMD_REAUTH_REQUIRED") {
    return {
      id: "github",
      name: "GitHub",
      description: "Create repositories, open pull requests, and manage workflow runs.",
      status: "error",
      authReady: false,
      authReason: "cmmd_reauth_required",
      connectionKind: "github",
    };
  }
  if (!response.ok) {
    throw new Error(payload.error ?? "Unable to refresh GitHub connection");
  }
  return githubStatusToPlugin(payload);
}

export async function fetchPlugins(): Promise<ReadonlyArray<ForgePlugin>> {
  const [connectors, githubPlugin] = await Promise.all([
    fetchWithTimeout("/api/cmmd/connectors", { headers: { Accept: "application/json" } }),
    fetchGitHubPlugin(),
  ]);
  if (!connectors.ok) {
    throw new Error(`Unable to refresh plugin connections (${connectors.status})`);
  }
  const payload = (await connectors.json()) as PluginsResponse;
  return sortedPlugins([
    ...(payload.connectors ?? payload.data?.connectors ?? []).map(mergePluginDefaults),
    githubPlugin,
  ]);
}

async function fetchInstallUrl(pluginId: ForgePluginId): Promise<InstallResponse> {
  const response = await fetch(`/api/cmmd/connectors/${encodeURIComponent(pluginId)}/install`, {
    method: "POST",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    body: JSON.stringify({ returnUrl: "/forge/plugins/callback" }),
  });
  const payload = (await response.json().catch(() => ({}))) as InstallResponse;
  if (!response.ok) {
    throw new Error(payload.message ?? payload.error ?? "Unable to start plugin connection");
  }
  return payload;
}

/**
 * Resolve a Forge runtime path against the PRIMARY runtime, not the page origin.
 *
 * In desktop cloud mode the renderer is served from the local desktop/Vite
 * origin while the selected primary runtime is a cloud URL behind a bearer
 * token. A bare same-origin `fetch` therefore sends these requests to the wrong
 * host AND drops the `Authorization` header, so GitHub OAuth cannot start for a
 * cloud runtime. See AGENTS.md "primary runtime" routing.
 *
 * Falls back to the relative path when no primary target resolves — that is the
 * plain browser case where the runtime serves this page itself, and the
 * relative path is already correct. Without the fallback this would throw and
 * break the common path to fix the uncommon one.
 */
function primaryRuntimeUrl(pathname: string, searchParams?: Record<string, string>): string {
  try {
    return resolvePrimaryEnvironmentHttpUrl(pathname, searchParams);
  } catch {
    const query = searchParams ? `?${new URLSearchParams(searchParams).toString()}` : "";
    return `${pathname}${query}`;
  }
}

export async function fetchGitHubAuthorizeUrl(): Promise<InstallResponse> {
  // Pass our origin so CMMD's callback returns the user to Forge (allow-listed
  // server-side) instead of landing them on the CMMD site.
  const returnTo = typeof window !== "undefined" ? window.location.origin : "";
  const response = await fetch(
    primaryRuntimeUrl(
      "/.well-known/forge/github/authorize-url",
      returnTo ? { return_to: returnTo } : undefined,
    ),
    buildPrimaryFetchInit({
      headers: { Accept: "application/json" },
      credentials: "include",
    }),
  );
  const payload = (await response.json().catch(() => ({}))) as GitHubAuthorizeResponse;
  if (!response.ok) {
    throw new Error(payload.message ?? payload.error ?? "Unable to start GitHub connection");
  }
  return typeof payload.url === "string" ? { authUrl: payload.url } : {};
}

export async function beginPluginAuthorization(pluginId: ForgePluginId): Promise<InstallResponse> {
  return pluginId === "github" ? fetchGitHubAuthorizeUrl() : fetchInstallUrl(pluginId);
}

export async function disconnectGitHubConnection(): Promise<void> {
  const response = await fetch(
    primaryRuntimeUrl("/.well-known/forge/github/connection"),
    buildPrimaryFetchInit({
      method: "DELETE",
      headers: { Accept: "application/json" },
      credentials: "include",
    }),
  );
  if (!response.ok) {
    const payload = (await response.json().catch(() => ({}))) as GitHubAuthorizeResponse;
    throw new Error(payload.message ?? payload.error ?? "Unable to disconnect GitHub");
  }
}

export async function disconnectPluginConnection(pluginId: ForgePluginId): Promise<void> {
  if (pluginId !== "github") {
    throw new Error(`${pluginId} does not support disconnect`);
  }
  await disconnectGitHubConnection();
}
